// src/hooks/useProperties.js
import { useState, useEffect, useCallback } from "react";
import { getProperties, getUserProperties } from "../utils/api";

/**
 * Hook to fetch and filter properties from Firestore.
 * Supports pagination via "Load More".
 */
export function useProperties(filters = {}) {
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastDoc, setLastDoc] = useState(null);
  const [hasMore, setHasMore] = useState(false);

  const fetchProperties = useCallback(async (reset = false) => {
    setLoading(true);
    setError(null);
    try {
      const result = await getProperties({
        ...filters,
        lastDoc: reset ? null : lastDoc,
      });
      setProperties(prev => reset ? result.properties : [...prev, ...result.properties]);
      setLastDoc(result.lastDoc);
      setHasMore(result.hasMore);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [JSON.stringify(filters)]); // eslint-disable-line

  useEffect(() => {
    fetchProperties(true);
  }, [JSON.stringify(filters)]); // eslint-disable-line

  const loadMore = () => fetchProperties(false);
  const refresh = () => fetchProperties(true);

  return { properties, loading, error, hasMore, loadMore, refresh };
}

/**
 * Hook to fetch all properties owned by the current user.
 */
export function useMyProperties(userId) {
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!userId) { setLoading(false); return; }
    getUserProperties(userId)
      .then(setProperties)
      .finally(() => setLoading(false));
  }, [userId]);

  return { properties, loading, setProperties };
}
